import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { MapPin, Megaphone, ExternalLink, ChevronDown, Loader2 } from "lucide-react";

const Stat = ({ label, value, testid }) => <div className="rounded-lg bg-secondary/40 px-3 py-2" data-testid={testid}>
  <p className="text-lg font-semibold tabular-nums">{value}</p><p className="text-xs text-muted-foreground">{label}</p>
</div>;

const DealerRow = ({ d }) => <li className="flex items-center justify-between gap-2 py-1.5 text-sm" data-testid={`dealer-map-row-${d.id}`}>
  <span className="min-w-0 truncate">{d.name}{d.city && <span className="text-muted-foreground"> · {d.city}</span>}</span>
  <span className="flex shrink-0 gap-1">
    <Badge variant="outline" className="text-[10px]">{d.opted_in ? "Ha scelto" : "Non ha scelto"}</Badge>
    <Badge variant={d.published ? "default" : "secondary"} className="text-[10px]" data-testid={`dealer-map-row-status-${d.id}`}>{d.published ? "Sulla mappa" : d.located ? "Nascosto" : "Senza posizione"}</Badge>
  </span>
</li>;

export const DealerMapAdmin = () => {
  const [v, setV] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/dealer-map").then((r) => setV(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!v?.locating) return undefined;
    const t = setTimeout(load, 3000);
    return () => clearTimeout(t);
  }, [v]);
  const run = async (call, msg) => {
    setBusy(true);
    try { const { data } = await call(); setV(data); toast.success(msg); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Operazione non riuscita"); }
    finally { setBusy(false); }
  };
  if (!v) return null;
  const c = v.counts;
  return <div className="space-y-3" data-testid="dealer-map-admin">
    <div className="flex items-center justify-between gap-3">
      <div><h2 className="font-head text-lg font-semibold">Mappa dealer pubblica</h2><p className="text-xs text-muted-foreground mt-0.5">Pubblica la pagina /dealers con i dealer che hanno scelto di comparire dalla loro vetrina.</p></div>
      <Switch checked={v.enabled} disabled={busy} onCheckedChange={(x) => run(() => api.put("/admin/dealer-map", { enabled: x }), x ? "Mappa dealer pubblicata" : "Mappa dealer nascosta")} data-testid="dealer-map-switch" />
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      <Stat label="Sulla mappa" value={c.published} testid="dealer-map-count-published" />
      <Stat label="Hanno scelto di comparire" value={c.opted_in} testid="dealer-map-count-opted" />
      <Stat label="Dealer approvati" value={c.total} testid="dealer-map-count-total" />
      <Stat label="Senza posizione" value={c.missing_location} testid="dealer-map-count-missing" />
    </div>
    <div className="flex items-center justify-between gap-3 rounded-lg border border-border p-3">
      <div><p className="text-sm font-medium">Mostra tutti i dealer</p><p className="text-xs text-muted-foreground">Pubblica anche chi non l'ha scelto. Se lo spegni, conta di nuovo la scelta di ogni dealer.</p></div>
      <Switch checked={v.force_all} disabled={busy} onCheckedChange={(x) => run(() => api.put("/admin/dealer-map", { force_all: x }), x ? "Ora la mappa mostra tutti i dealer" : "La mappa mostra solo chi l'ha scelto")} data-testid="dealer-map-force-switch" />
    </div>
    <div className="compact-actions"><div className="flex flex-wrap items-center gap-2">
      {!(v.enabled && v.force_all) && <Button size="sm" className="gap-1.5" disabled={busy} onClick={() => run(() => api.post("/admin/dealer-map/publish-all"), "Tutti i dealer approvati sono pubblicati")} data-testid="dealer-map-publish-all"><Megaphone className="w-4 h-4" />Pubblica tutti adesso</Button>}
      {c.missing_location > 0 && <Button size="sm" variant="outline" className="gap-1.5" disabled={busy || v.locating} onClick={() => run(() => api.post("/admin/dealer-map/locate"), "Cerco le posizioni dagli indirizzi")} data-testid="dealer-map-locate">{v.locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}{v.locating ? "Cerco le posizioni…" : "Trova posizioni mancanti"}</Button>}
      <Button size="sm" variant="ghost" className="gap-1.5" asChild><a href="/dealers" target="_blank" rel="noreferrer" data-testid="dealer-map-open"><ExternalLink className="w-4 h-4" />Apri mappa</a></Button>
      <Button size="sm" variant="ghost" className="gap-1.5" onClick={() => setOpen(!open)} data-testid="dealer-map-list-toggle"><ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />Elenco dealer ({c.total})</Button>
    </div></div>
    {open && <ul className="divide-y divide-border max-h-72 overflow-auto" data-testid="dealer-map-list">{v.dealers.map((d) => <DealerRow key={d.id} d={d} />)}</ul>}
  </div>;
};
